import React, { useContext, useRef, useState } from 'react';

import { HelpOutline } from '@mui/icons-material';
import MaterialGrid from '@mui/material/Grid/Grid';
import isMobile from 'ismobilejs';

import { MainContext, useTranslation } from '@ezbobdev/ui-framework';

import LanguageSelect from '../LanguageSelect/LanguageSelect';
import MaterialLink from '../MaterialLink/MaterialLink';
import ContactForm from './ContactForm/ContactForm';
import { CustomPopoverElement } from './Popovers/CustomPopoverElement';
import { ExitPopoverContent } from './Popovers/ExitPopoverContent';
import { HelpPopoverContent } from './Popovers/HelpPopoverContent';
import { PhonePopoverContent } from './Popovers/PhonePopoverContent';
import QnAPopup from './QnAPopup/QnAPopup';

const MaterialAppBarActions = (props) => {
  const { alloWMultilanguages, customerWizardRoute, help, exit, phone, logoutButton, helpType, configByName } = props;

  const { actions } = useContext(MainContext);

  const { t } = useTranslation();

  const [isContactFormDialogOpen, setIsContactFormDialogOpen] = useState(false);
  const [isQnAPopupOpen, setIsQnAPopupOpen] = useState(false);

  const helpState = useState(null);
  const exitState = useState(null);
  const phoneState = useState(null);
  const [, setHelpState] = helpState;
  const [, setExitState] = exitState;
  const [, setPhoneState] = phoneState;

  const phonePopoverRef = useRef();
  const helpPopoverRef = useRef();
  const exitPopoverRef = useRef();

  const handleHelpPopoverOpen = () => setHelpState(helpPopoverRef.current);
  const handleExitPopoverOpen = () => setExitState(exitPopoverRef.current);
  const handlePhonePopoverOpen = () => setPhoneState(phonePopoverRef.current);

  const handleExitModalOpen = () => {
    actions.view.toggleModal({ modalName: exit?.modalName });
  };

  const handleQnAPopupOpen = () => {
    setIsQnAPopupOpen(true);
    setHelpState(null);
  };

  const handeFormDialogOpen = () => {
    setIsContactFormDialogOpen(true);
    setHelpState(null);
  };

  const handleQnAPopupClose = () => setIsQnAPopupOpen(false);

  const handleFormDialogClose = () => {
    setIsContactFormDialogOpen(false);
    actions.case.resetInCase([
      'contactForm.fullName.firstName',
      'contactForm.fullName.lastName',
      'contactForm.id',
      'contactForm.phone',
      'contactForm.description',
    ]);
  };

  const shouldShowExit = () => {
    if (!exit) return false;

    const currentStep = actions.userState.getCurrentStepName();

    if (exit.hideOnPages && exit.hideOnPages.includes(currentStep)) return false;

    if (exit.showOnlyOnMobile) return isMobile().any;

    return true;
  };

  const phoneProps = {
    iconSrc: phone?.iconSrc,
    iconAlt: t(`${configByName}.phone.iconAlt`),
    state: phoneState,
    label: t(`${configByName}.phone.popover.title`),
    handleOpen: handlePhonePopoverOpen,
    content: <PhonePopoverContent iconSrc={phone?.popover?.iconSrc} configByName={configByName} />,
  };

  const helpProps = {
    iconSrc: help?.iconSrc,
    iconAlt: t(`${configByName}.help.iconAlt`),
    state: helpState,
    label: t(`${configByName}.help.popover.title`),
    handleOpen: handleHelpPopoverOpen,
    content: (
      <HelpPopoverContent
        iconSrc={help?.popover?.iconSrc}
        configByName={configByName}
        phoneIconSrc={help?.popover?.phoneIconSrc}
        mailIconSrc={help?.popover?.mailIconSrc}
        QnAIconSrc={help?.popover?.QnAIconSrc}
        declarationLink={help?.popover?.declarationLink}
        handlePhonePopoverOpen={() => {
          setHelpState(null);
          handlePhonePopoverOpen();
        }}
        onFormIconClick={handeFormDialogOpen}
        onQnAIconClick={handleQnAPopupOpen}
      />
    ),
  };

  const exitProps = {
    iconSrc: exit?.iconSrc,
    iconAlt: t(`${configByName}.exit.iconAlt`),
    label: t(`${configByName}.exit.popover.title`),
    handleOpen: exit?.variant === 'modal' ? handleExitModalOpen : handleExitPopoverOpen,
    state: exitState,
    content: (
      <ExitPopoverContent
        handleExitPopoverOpen={() => {
          setExitState(null);
        }}
        iconSrc={exit?.popover?.iconSrc}
        configByName={configByName}
        overrideLogoutUrl={exit?.overrideLogoutUrl}
      />
    ),
  };

  return (
    <MaterialGrid container columnGap={3}>
      {!!phone && (
        <MaterialGrid item>
          <CustomPopoverElement ref={phonePopoverRef} {...phoneProps} />
        </MaterialGrid>
      )}

      {help?.type === helpType.page && !help?.isHidden && (
        <MaterialGrid item display="flex" justifyContent="center" alignItems="center">
          <MaterialLink
            href={customerWizardRoute.url}
            color="inherit"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 0 }}
            aria-label={t(`globals.help`)}
          >
            <HelpOutline />
          </MaterialLink>
        </MaterialGrid>
      )}

      {help?.type === helpType?.popover && (
        <MaterialGrid item>
          <CustomPopoverElement ref={helpPopoverRef} {...helpProps} />
        </MaterialGrid>
      )}

      {alloWMultilanguages && (
        <MaterialGrid item display="flex" justifyContent="center" alignItems="center">
          <LanguageSelect
            onChange={() => {
              setTimeout(() => {
                window.location.reload();
              }, 100);
            }}
          />
        </MaterialGrid>
      )}

      {!exit && logoutButton}
      {shouldShowExit() && (
        <MaterialGrid item>
          <CustomPopoverElement ref={exitPopoverRef} {...exitProps} />
        </MaterialGrid>
      )}
      <QnAPopup open={isQnAPopupOpen} onClose={handleQnAPopupClose} />
      <ContactForm open={isContactFormDialogOpen} onClose={handleFormDialogClose} />
    </MaterialGrid>
  );
};

export default MaterialAppBarActions;
